import { useState } from "react";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { API, formatApiErrorDetail } from "@/lib/api";
import { FadeUp, LineReveal } from "@/components/Reveal";

const serviceTypes = [
  "Collision Repair",
  "Vehicle Restoration",
  "Custom Paint & Refinish",
  "Dent & Scratch Repair",
  "Other / Not Sure",
];

const empty = { name: "", email: "", phone: "", vehicle: "", service_type: "", description: "" };

export default function Quote() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/quotes`, form);
      setDone(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="quote-page">
      <section className="px-6 md:px-12 pt-40 md:pt-52 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <FadeUp>
              <p className="text-xs uppercase tracking-[0.35em] text-[#E51B23] mb-6">Free Estimate</p>
            </FadeUp>
            <h1 className="font-display uppercase leading-[0.9] text-[14vw] lg:text-7xl">
              <LineReveal text="Straight" delay={0.1} />
              <LineReveal text="Numbers Only" delay={0.25} className="text-outline" />
            </h1>
            <FadeUp delay={0.4}>
              <p className="mt-8 text-zinc-400 text-sm md:text-base leading-relaxed max-w-md">
                Describe the damage or the project and we'll come back with an honest
                estimate — no padding, no pressure. Photos help; you can bring them when
                you visit.
              </p>
            </FadeUp>
          </div>

          <FadeUp delay={0.2} className="lg:col-span-7">
            {done ? (
              <div className="border border-zinc-800 bg-[#131A2E] p-10 md:p-14 text-center" data-testid="quote-success">
                <CheckCircle2 size={48} className="text-[#E51B23] mx-auto" />
                <h2 className="font-display uppercase text-3xl md:text-4xl mt-6">Estimate Requested</h2>
                <p className="text-zinc-400 text-sm mt-4 max-w-md mx-auto leading-relaxed">
                  Thanks {done.name} — your estimate request for the {done.vehicle} is in
                  the queue. We'll reply within one business day.
                </p>
                <button
                  data-testid="quote-another-button"
                  onClick={() => { setDone(null); setForm(empty); }}
                  className="osc-btn-primary mt-8"
                >
                  Request Another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-zinc-800 bg-[#131A2E] p-8 md:p-12" data-testid="quote-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="osc-label" htmlFor="quote-name">Full Name</label>
                    <input id="quote-name" data-testid="quote-name-input" required className="osc-input" value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                  </div>
                  <div>
                    <label className="osc-label" htmlFor="quote-phone">Phone</label>
                    <input id="quote-phone" data-testid="quote-phone-input" required type="tel" className="osc-input" value={form.phone} onChange={set("phone")} placeholder="(250) 555-0000" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="osc-label" htmlFor="quote-email">Email</label>
                    <input id="quote-email" data-testid="quote-email-input" required type="email" className="osc-input" value={form.email} onChange={set("email")} placeholder="jane@example.com" />
                  </div>
                  <div>
                    <label className="osc-label" htmlFor="quote-vehicle">Vehicle (Year / Make / Model)</label>
                    <input id="quote-vehicle" data-testid="quote-vehicle-input" required className="osc-input" value={form.vehicle} onChange={set("vehicle")} placeholder="2021 Subaru WRX" />
                  </div>
                  <div>
                    <label className="osc-label" htmlFor="quote-service">Service Type</label>
                    <select id="quote-service" data-testid="quote-service-select" required className="osc-input" value={form.service_type} onChange={set("service_type")}>
                      <option value="" disabled>Select a service</option>
                      {serviceTypes.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="osc-label" htmlFor="quote-description">Describe The Work</label>
                    <textarea id="quote-description" data-testid="quote-description-input" required rows={5} className="osc-input resize-none" value={form.description} onChange={set("description")} placeholder="What happened, or what do you want done? The more detail, the sharper the estimate." />
                  </div>
                </div>

                {error && <p className="mt-6 text-sm text-red-400" data-testid="quote-error">{error}</p>}

                <button
                  type="submit"
                  data-testid="quote-submit-button"
                  disabled={loading}
                  className="osc-btn-primary mt-8 w-full sm:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Sending..." : "Request Free Estimate"}
                </button>
              </form>
            )}
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
